import { Autocomplete, Grid, Tooltip, autocompleteClasses } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import { getStrDateOfDateObj, isEmptyObject } from "../MainPage/Tools/Tools";
import { IMaskInput } from "react-imask";
import { AxiosRequest } from "../Url";

import DateCalendarEditor from "./EditorComponents/DateCalendarEditor";
import CalendarButton from "./EditorComponents/CalendarButton";
import UpDownButtons from "./EditorComponents/UpDownButtons";
import PickListEditor from "./EditorComponents/PickListEditor";
import ModalButton from "./EditorComponents/ModalButton";
import CheckBoxMultiCheck from "../MainPage/Sections/ElementsSections/CheckBoxMultiCheck";
import CheckboxList from "./EditorComponents/CheckBoxList";
import DetailsButton from "./EditorComponents/DetailsButton";
import CustomCheckBoxList from "./EditorComponents/CustomCheckList";
import { colorList } from "./EditorComponents/ColorTools";
import { SetLoadingCursor } from "../TokenProcessing/TokenProcessing";

// запрос для выпадающего списка
async function getParamValues(props, editor, checkList) {
  // внешний запрос
  if (props.onDropDownList) {
    SetLoadingCursor(true);
    const result = await props.onDropDownList(
      {
        ...props,
        module: props.type,
        events: props.onEvents,
        ID: props.id,
        editor: editor,
      },
      checkList,
    );
    SetLoadingCursor(true, undefined, true);

    if (isEmptyObject(result)) {
      return [{ label: "Нет данных", id: "-1", disabled: true }];
    }

    return result;
  } else return [{ label: "Нет данных", id: "-1", disabled: true }];
}

// Функция для открытия модального окна
function openModalWindow(props, json, subData) {
  if (props.onEditButtonClick) {
    props.onEditButtonClick({
      ...props,
      events: props.onEvents,
      objref: subData.props.ObjRef ? subData.props.ObjRef : props.objref,
      json: json,
      subData: subData,
      setValue: subData.props.setValue,
      setInputValue: subData.props.setInputValue,
    });
  }
}
// запрос для данных модального окна
const GetDialogParam = (module, ObjType, objref, multisel) => {
  let param = new Map(),
    json;
  param.set("prefix", module);
  param.set("comand", "GetDialogParams");
  param.set("ObjType", ObjType);
  param.set("ObjRef", objref);
  if (multisel) param.set("MultiSel", "1");
  json = AxiosRequest(true, param);
  return json;
};

// функции для кнопок инкремента и декремента
function incValue(inputValue, setValue, valueChanged) {
  let val = Number(inputValue) || 0;
  if (Number.isNaN(val)) return;
  setValue(`${val + 1}`);
  valueChanged.current = true;
}
function decValue(value, setValue, valueChanged) {
  let val = Number(value) || 0;
  if (Number.isNaN(val)) return;
  setValue(`${val - 1}`);
  valueChanged.current = true;
}

// удаление разделителя из значения для редактора (он добавляется с помощью маски)
function fixValue(value) {
  if (value !== undefined) {
    let val = value.match(/[0-9]/g);
    if (!val) return "";
    val = val.join("");
    return val;
  }
  return "";
}

function toHex(i) {
  let res = 0xff & i;
  if (res) {
    res = res.toString(16);
  } else res = 0;
  res = String(res).padStart(2, "0");
  return res;
}

export function setColor(intColor) {
  let result = `#${toHex(intColor)}${toHex(intColor >> 8)}${toHex(intColor >> 16)}`;
  return result;
}

// Входные данные:
// editStyle - свойство определяющее какие кнопки будут отрисованы
// objref - id элемента нужен для позиционирования в справочниках списках и тд
// value - входное значение инпута
// onEdit - функция которая вызывается на любое редактирование
// onDropDownList - функция вызывается на клик по кнопке списка(editstyle === 1) и должна возвращать список в виде [{ label: "название", id: "objref или другой идентификатор", disabled: true(опционально делает элемент недоступным в списке) }]
// onEditButtonClick - функция для клика на кнопку открытия диалогового окна
// onEditList - функция вызывается при не дефолтном поведении на клик элемента выпадающего списка
// onChange - функция вызывется при изменении значения inputValue
// addInfo - добавочные данные для редактирования таблиц
// list - список в виде Elemen1\r\nElement2\r\n...(если список получается не по запросу а от внешнего компонента)
// placeholder - не стандартный заголовок
// editor - объект в который запишется функция редактирования
// getEdition - функция в который запишутся хуки управления состоянием значения инпута
// grid - отменяет обычное редактирование и создает отдельный слушатель событий на clickAway
// clickAway - функция которая вызывается в отдельном слушателе событий при отмене стандартной
// disabled - свойство отключает полностью весь функционал редактора
// readOnly - отключает ввод с клавиатуры (кнопки работают)
// record - элемент в котором хранятся данные для внешнего редактирования(Например в параметрах)
// остальные параметры используются только для передачи в функции onEdit, onEditButtonClick, onDropDownList (при необходимости добавить новые, их нужно добавлять в ручную везде, где в них есть потребность)

// Общий прицип работы: Внешние данные и значение -> Editor -> редактирование(включая выбор из списка) -> Вызов функции onEdit(Новое значение и внешние данных переданные изначально)
//                                                          -> редактирование через модальное окно -> Вызов функций onEditButtonClick(пердача всех нужных данных, включая функцию onEdit) -> редактирование -> Вызов функции onEdit внутри компонента с модальным окном(Новое значение и внешние данных переданные изначально)

export default function Editor(props) {
  const EditStyle_PickList = 1 & props.editStyle; // Выпадающий список реализован
  const EditStyle_Calendar = 2 & props.editStyle; // Календарь реализован
  const EditStyle_Check = 4 & props.editStyle; // Чекбокс реализован
  const EditStyle_UpDown = 8 & props.editStyle; // Инкремент и Декремент реализован
  const EditStyle_Ellipsis = 16 & props.editStyle; // Кнопка открытия модального окна реализована
  const EditStyle_CheckList = 32 & props.editStyle; // Список с множественным выбором реализован
  const EditStyle_ReadOnly = 64 & props.editStyle; // Только для чтения(работает только для ввода с клавиатуры, кнопки будут работать штатно)
  const EditStyle_EditList = 128 & props.editStyle; // не реализовано
  const EditStyle_ColorBox = 256 & props.editStyle; // не реализовано
  const EditStyle_AutoList = 512 & props.editStyle; // не реализовано
  const EditStyle_Password = 1024 & props.editStyle; // не реализовано
  const EditStyle_CustomCheckList = props.editStyle === "customCheckList";
  const EditStyle_CustomColorList = props.editStyle === "customColorList";

  const autocompleteList = EditStyle_PickList || EditStyle_CustomColorList || EditStyle_ColorBox;

  const popover = autocompleteList || EditStyle_CheckList || EditStyle_CustomCheckList || EditStyle_Calendar;

  // ссылка на кнопку модального окна
  const modalButton = React.useRef();
  const buttonCalendar = React.useRef(null);
  const buttonPickList = React.useRef(null);
  const buttonDetails = React.useRef(null);

  const mainBlock = React.useRef();

  const [styleInput, setStyleInput] = React.useState({});

  //////////////////////////////////////////////////////////////////////////////
  // регекс проверки правильности написания даты
  const dateRegexp =
    /^(?:(?:31(\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\.)(?:0?[1,3-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/;

  // функции для Popover(выпадающим полем) с выбором даты
  const [anchorElDate, setAnchorElDate] = React.useState(null);
  // клик на дату
  const handleClickDate = (event) => {
    if (EditStyle_Ellipsis) {
      modalButton.current.click();
      return;
    }
    setAnchorElDate(event.currentTarget);
    if (EditStyle_PickList) setOpenList(false);
  };
  // функция на закрытие
  const handleCloseDate = () => {
    setAnchorElDate(null);
  };
  // переменные контроля открытия даты
  const openDate = Boolean(anchorElDate);
  const idDate = openDate ? "calendar" : undefined;
  //////////////////////////////////////////////////////////////////////////////

  // функции для Popover с чеклистом
  const [anchorElForCheckList, setAnchorElForCheckList] = React.useState(null);
  // функции на клик и закрытие выпадающего списка
  const handleClickForCheckList = (event) => {
    setAnchorElForCheckList(event.currentTarget);
  };
  const handleCloseForCheckList = async () => {
    // valueChanged.current = true;
    setAnchorElForCheckList(null);

    if (EditStyle_CustomCheckList) {
      return;
    }
    await props.onEdit({
      record: props.record,
      tag: checkListObjRef.current,
      value: inputValue,
      textchanged: "0",
      setValue: setValue,
      setInputValue: setInputValue,
      addInfo: props.addInfo,
      checkList: true,
    });
  };

  // переменные контроля чеклиста
  const openForCheckList = Boolean(anchorElForCheckList);
  const idForCheckList = openForCheckList ? "checklist" : undefined;

  // состояние элементов списка
  const [checkedList, setCheckedList] = React.useState([]);
  const checkListObjRef = React.useRef();

  const handleToggle = (ev, value) => {
    const currentIndex = checkedList.indexOf(Number(value.id));
    const indexItem = [...ev.target.closest("UL").children].indexOf(ev.target.closest("LI"));
    const newChecked = [...checkedList];

    if (currentIndex === -1) {
      newChecked[indexItem] = Number(value.id);
      checkListObjRef.current = Number(checkListObjRef.current) + Number(value.id);
    } else {
      newChecked[indexItem] = -1;
      checkListObjRef.current = Number(checkListObjRef.current) - Number(value.id);
    }

    setInputValue(getCheckListInputValue(newChecked));

    setCheckedList(newChecked);
  };

  function getCheckListInputValue(newChecked) {
    let result = [];
    paramValuesItems.forEach((item, index) => {
      if (newChecked[index] !== -1) {
        result.push(item.label);
      }
    });
    return result.join(",");
  }
  //////////////////////////////////////////////////////////////////////////////

  // состояние открытия выпадающего списка
  const [openList, setOpenList] = React.useState(false);

  // размеры контейнеров внутри editor
  const [width, setWidth] = React.useState({
    input: "100%",
    start: "0%",
    end: "0%",
  });

  const [filter, setFilter] = React.useState("");
  const filterDefaultValues = useRef([{ label: "Нет данных", id: "-1", disabled: true }]);

  // внутреннее значение в виде {label, id}
  const [value, setValue] = React.useState({});

  // значение видимого input в виде string
  const [inputValue, setInputValue] = React.useState("");
  // const [checkState, setCheckState] = React.useState(props.checked);

  const [selection, setSelection] = React.useState(props.selection);

  // ссылка на контейнер с элементами в начале редактора
  const startContentRef = React.useRef(null);
  // ссылка на контейнер с элементами в конце редактора
  const endContentRef = React.useRef(null);
  // ссылка на input
  const input = React.useRef(null);
  // ссылка на маску input
  const maskRef = React.useRef(null);
  // ссылка на корневой элемент
  const autoCompleteValue = React.useRef(null);

  const [placeholder, setPlaceHolder] = React.useState("");

  // переменная для контроля изменения в input
  const valueChanged = React.useRef();
  const blurValue = React.useRef();

  // Данные для выпадающего списка
  const [paramValuesItems, setParamValues] = React.useState([{ label: "Нет данных", id: "-1", disabled: true }]);

  // переменная для контроля фокуса
  const focus = React.useRef();

  const setCustomColorList = async () => {
    if (props.onListCustom) {
      const list = await props.onListCustom();

      filterDefaultValues.current = list.map((item) => {
        return {
          label: item.Name,
          id: item.Mask,
          color: setColor(Number(item.Color)),
        };
      });

      setParamValues(filterDefaultValues.current);
    }

    setOpenList(!openList);
  };

  const setColorList = () => {
    filterDefaultValues.current = colorList;
    setParamValues(colorList);
    setOpenList(!openList);
  };

  const setCustomCheckList = async (ev) => {
    filterDefaultValues.current = await props.getItems();
    setParamValues(filterDefaultValues.current);
    handleClickForCheckList(ev);
    setAnchorElForCheckList(ev.target);
  };

  const setCheckList = async (ev) => {
    // чеклист
    if (!props.onlist && props.listValues && props.listValues.length) {
      filterDefaultValues.current = props.listValues;
      setParamValues(props.listValues);
      handleClickForCheckList(ev);
      setAnchorElForCheckList(ev.target);
      return;
    }

    if (!props.onlist && props.list && props.list !== "") {
      const list = props.list.split("\r\n");
      if (list.length && list[list.length - 1] === "") list.pop();
      filterDefaultValues.current = list.map((item, index) => {
        return { label: item, id: Math.pow(2, index) };
      });
      setParamValues(filterDefaultValues.current);
    } else {
      filterDefaultValues.current = await getParamValues(props, autoCompleteValue.current, true);
      setParamValues(filterDefaultValues.current);
    }
    handleClickForCheckList(ev);
    setAnchorElForCheckList(ev.target);
  };

  const setPickList = async () => {
    // обычный список
    if (!props.onlist && props.listValues && props.listValues.length) {
      filterDefaultValues.current = props.listValues;
      setParamValues(props.listValues);
      setOpenList(!openList);
      return;
    }

    if (!props.onlist && props.list && props.list !== "") {
      const list = props.list.split("\r\n");
      if (list.length && list[list.length - 1] === "") list.pop();
      filterDefaultValues.current = list.map((label, id) => ({ label, id }));
      setParamValues(filterDefaultValues.current);
    } else {
      filterDefaultValues.current = await getParamValues(props, autoCompleteValue.current);
      setParamValues(filterDefaultValues.current);
    }
    setOpenList(!openList);
  };

  // функция для кнопки открытия выпадающего списка
  const onPickListClick = async (ev) => {
    if (EditStyle_CustomColorList) {
      setCustomColorList();
      return;
    }
    if (EditStyle_ColorBox) {
      setColorList();
      return;
    }
    if (EditStyle_CustomCheckList) {
      setCustomCheckList(ev);
      return;
    }
    // заполнение выпадающего списка данными
    if (EditStyle_CheckList) {
      setCheckList(ev);
      return;
    }

    EditStyle_PickList && setPickList();
  };

  const openAfterRender = (command) => {
    if (modalButton.current || buttonPickList.current || buttonCalendar.current) {
      const mButton = modalButton.current;
      const lButton = (EditStyle_Calendar && buttonCalendar.current) || buttonPickList.current;
      const comands = [() => EditStyle_Ellipsis && mButton.click(), () => popover && lButton.click()];
      comands[command]?.();
    }
  };

  React.useEffect(() => {
    mainBlock.current.changeValue = changeInputValue;

    props.openAfterRender !== undefined && openAfterRender(props.openAfterRender);

    setValue({
      label: props.editMask && EditStyle_Calendar === 0 ? fixValue(props.value) : props.value,
      id: props.objref,
    });
    setInputValue(props.editMask && EditStyle_Calendar === 0 ? fixValue(props.value) : props.value);
  }, []);

  React.useEffect(() => {
    if (inputValue === undefined) setInputValue("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  // useEffect(() => {}, [value]);

  // действия после монтирования компонента
  React.useEffect(() => {
    // установка стилей кнопок
    // if (startContent === 0 && endContent === 0) {
    getInnerButtons();
    // }
    // установка заголовка, если он есть
    if (EditStyle_Check) {
      setPlaceHolder("");
    } else if (props.editMask && EditStyle_Calendar === 0) {
      setPlaceHolder(props.editMask.replaceAll("#", "_").replaceAll("0", "_"));
    } else {
      setPlaceHolder(props.placeholder !== undefined ? props.placeholder : "не выбрано");
    }
    // определение переменной следящей за фокусом на инпуте
    focus.current = false;

    // установка первичного значения для чек листа
    if (EditStyle_CheckList) {
      checkListObjRef.current = props.objref ? props.objref : "0";
    }

    // передача функции редактирования на внешние модули
    if (props.editor) {
      props.editor.onEdit = onEditValue;
    }

    setStyleInput({
      margin: "0",
      height: "100%",
      width: "100%",
      color:
        EditStyle_ReadOnly === 64 || props.readonly || props.disabled || props.HardCodeReadOnlyForEditor
          ? "#a8a6a6"
          : "#212529",
      border: "0",
      borderRadius: "0",
      outline: "none",
      // padding: "0",
      fontSize: "0.75rem",
      fontFamily: "Roboto",
      letterSpacing: "0px",
      backgroundColor: props.background ? "whitesmoke" : "inherit",
      ...props.styleInput,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  // Передача функций установки значений и дополнительных данных на внешние модули
  React.useEffect(() => {
    if (props.getEdition && props.record) {
      props.getEdition({
        setValue,
        setInputValue,
        value: value,
        inputValue: inputValue,
        id: props.record.id,
        key: props.record.dataset.key,
        setSelection,
      });
    } else if (props.getEdition) {
      props.getEdition({
        setValue,
        setInputValue,
        value: value,
        inputValue: inputValue,
        setSelection,
      });
    }
  }, [props.getEdition]);

  // обновление состояния чеклиста
  React.useEffect(() => {
    if (paramValuesItems && EditStyle_CheckList && checkedList.length === 0 && !paramValuesItems[0].disabled) {
      let objref = Number(props.objref ? props.objref : "0");

      setCheckedList(
        paramValuesItems.map((item) => {
          if (objref > 0 && objref & Number(item.id)) {
            objref -= Number(item.id);
            return Number(item.id);
          }
          return -1;
        }),
      );
    } else if (paramValuesItems && EditStyle_CheckList && value.id) {
      updateCheckList(value.id);
    }
  }, [paramValuesItems, value]);

  // отслеживание изменений значений value и objref снаружи
  React.useEffect(() => {
    setValue({
      label: props.editMask && EditStyle_Calendar === 0 ? fixValue(props.value) : props.value,
      id: props.objref,
    });
    setInputValue(props.editMask && EditStyle_Calendar === 0 ? fixValue(props.value) : props.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, props.objref]);

  useEffect(() => {
    if (!(EditStyle_ReadOnly === 64 || props.readonly)) return;
    if (filter !== "") {
      setParamValues(
        filterDefaultValues.current.filter((option) => {
          return option.label.toLowerCase().match(filter.toLowerCase());
        }),
      );
      return;
    }
    if (filter === "") {
      setParamValues(filterDefaultValues.current);
    }
  }, [filter]);

  function updateCheckList(objref) {
    if (objref === undefined) return;
    let id = Number(objref);
    setCheckedList(
      paramValuesItems.map((item) => {
        if (id > 0 && id & Number(item.id)) {
          id -= Number(item.id);
          return Number(item.id);
        }
        return -1;
      }),
    );
    checkListObjRef.current = objref;
  }

  // функция для установки нужного формата для new Date
  // YYYY-MM-DD
  function getDateInFormat(value) {
    let date = value;

    if (date) {
      if (date.match(/\d{4}-\d{2}-\d{2}/)) return date;
      if (date.match(/\d{2}.\d{2}.\d{4}/)) {
        date = date.split(".");
        date = [date[2], date[1], date[0]];
        return date.join("-");
      }
      return checkDateCompletion(date);
    }
  }

  // функция заполняет данные месяца и года, если они не были указаны
  function checkDateCompletion(date) {
    const now = new Date();
    const separator = date.includes(".") ? "." : "-";
    const value = date.split(separator).map((item) => item.replaceAll("_", ""));
    if (value.length === 3 && value.some((item) => item !== "")) {
      if (value[0].length === 4) [value[0], value[2]] = [value[2], value[0]];
      if (value[0] === "0") value[0] = "1";
      if (value[1] === "0") value[1] = "1";
      const currentYear = now.getFullYear().toString();
      const currentMonth = String(now.getMonth() + 1).padStart(2, "0");
      const year = currentYear.slice(0, 4 - value[2].length) + value[2];
      const month = value[1] ? value[1].padStart(2, "0") : currentMonth;
      const day = value[0] ? value[0].padStart(2, "0") : "01";
      return `${year}-${month}-${day}`;
    }
  }

  // функция для установки даты в формат для сервера
  // {label:DD.MM.YYYY, id:number}
  function getDateBack(value) {
    const date = getDateInFormat(value);

    if (date) {
      // // вычисление id даты (количество дней с 30.12.1899)
      const oneDay = 1000 * 60 * 60 * 24;
      const startDate = new Date("1899-12-30");
      const currentDate = new Date(date);
      let id = Math.round((currentDate.getTime() - startDate.getTime()) / oneDay);

      if (!id) id = "-1";
      return { label: value === ".." ? "" : getStrDateOfDateObj(currentDate), id: id };
    }
    return { label: "", id: "" };
  }

  // функция для отрисовки кнопок
  function getInnerButtons() {
    // количество кнопок в начале
    let startCounter = 0;
    // количество кнопок в конце
    let endCounter = 0;

    if (EditStyle_PickList) {
      endCounter++;
    }
    if (EditStyle_Calendar) {
      // особенность календаря, при стиле календарь и список - список заменяется на календарь
      if (EditStyle_PickList === 0) {
        endCounter++;
      }
    }
    if (EditStyle_Check) {
      startCounter++;
    }
    if (EditStyle_UpDown) {
      endCounter++;
    }
    if (EditStyle_Ellipsis) {
      if (EditStyle_Calendar === 0) {
        endCounter++;
      }
    }
    if (EditStyle_CheckList) {
      if (EditStyle_PickList === 0) endCounter++;
    }
    if (EditStyle_EditList) {
    }
    if (EditStyle_ColorBox) {
      if (EditStyle_PickList === 0) endCounter++;
    }
    if (EditStyle_AutoList) {
    }
    if (EditStyle_Password) {
    }

    if (EditStyle_CustomColorList) {
      if (EditStyle_PickList === 0) endCounter++;
    }
    if (EditStyle_CustomCheckList) {
      if (EditStyle_PickList === 0) endCounter++;
    }
    // обновление количества контента перед и после input
    setWidth({
      input: `calc(100% - ${startCounter * 27}px - ${endCounter * 20}px)`,
      start: `${startCounter * 27}px`,
      end: `${endCounter * 20}px`,
    });
  }

  // функция на фокус input
  function onFocus() {
    input.current?.select();
    focus.current = true;
    // для таблиц отдельный слушатель событий
    if (props.grid) {
      document.addEventListener("click", clickAway);
      input.current.deleteClickAway = () => document.removeEventListener("click", clickAway);
    }
  }
  // функция на потерю фокуса input
  function onBlur(ev) {
    if (ev.target.tagName === "INPUT" || ev.target.tagName === "TEXTAREA") {
      if (EditStyle_Calendar || props.datatype === "date") setInputValue(ev.target.value);
      focus.current = false;
      if (autocompleteList) setOpenList(false);
      if (props.placeholderVisible !== "1") input.current.placeholder = "";
      // для таблиц отдельная логика редактирования
      if (!props.grid) {
        // вызов функции редактирования
        onEditValue(undefined, ev.target.value ? ev.target.value : inputValue, "1");
      }
      // значение на потерю фокуса для функции onEditValue
      blurValue.current = ev.target.value;
    }
  }

  // Слушатель событий для таблиц на потерю фокуса
  function clickAway(ev) {
    if (
      ev.target === autoCompleteValue.current?.closest("TD") ||
      ev.target.closest("[componenteditor]") ||
      (ev.target.closest(".background_ModalDialog") &&
        ev.target.closest(".background_ModalDialog") ===
          autoCompleteValue.current?.closest(".background_ModalDialog")) ||
      ev.target.closest("[checklist]") ||
      ev.target.closest("[calendar]") ||
      ev.target.closest("[details]") ||
      ev.target.closest("[loadmask]") ||
      ev.target.classList.contains("MuiBackdrop-root")
    ) {
      return;
    }

    // если клик произошел на любое место кроме выше перечисленых выполнится код ниже
    if (props.clickAway) {
      // внешняя функция редактирования
      props.clickAway(ev, onEditValue, blurValue.current);
    }

    document.removeEventListener("click", clickAway);
  }

  // Функция для запроса на редактирование
  function onEditValue(ev, newValue, textchanged) {
    if (!props.onEdit || props.details) return;
    const editData = {
      path: props.path,
      type: props.type,
      datatype: props.datatype,
      name: props.name,
      input: input.current,
      proof: needProof(),
      record: props.record,
      groupid: props.groupid,
      fieldname: props.fieldname,
      current: props.current,
      textchanged: textchanged,
      setValue: setValue,
      setInputValue: setInputValue,
      addInfo: props.addInfo,
      onEdit: props.onEdit,
      fixVal: props.editMask && EditStyle_Calendar === 0 ? fixValue : undefined,
    };
    if (ev) {
      if (ev && (ev.key === "Enter" || ev.key === "NumpadEnter") && !props.grid) {
        if (typeof newValue === "object") {
          props.onEdit({
            ...editData,
            value: newValue.label,
            tag: newValue.id,
          });
          valueChanged.current = false;
          return;
        } else {
          const val = EditStyle_Calendar === 2 ? getDateBack(newValue) : { label: newValue };
          props.onEdit({
            ...editData,
            value: val.label,
            tag: val.id,
          });
        }
        valueChanged.current = false;
        return;
      }

      if (ev && (ev.key === "Enter" || ev.key === "NumpadEnter") && textchanged !== undefined) {
        if (typeof newValue === "object") {
          props.onEdit({
            ...editData,
            value: newValue.label,
            tag: newValue.id,
            proof: false,
          });
          valueChanged.current = false;
          return;
        }
      }

      if (ev.target && ev.target.tagName === "LI") {
        if (props.onEditList) {
          props.onEditList({
            current: props.current,
            tag: newValue.id,
            value: newValue.label,
            fieldname: props.fieldname,
            record: props.record,
            listClick: true,
            setValue: setValue,
            setInputValue: setInputValue,
          });
          valueChanged.current = false;
          return;
        }
        props.onEdit({
          ...editData,
          listClick: true,
          proof: false,
          value: newValue.label,
          tag: newValue.id,
          listindex: ev.target.dataset.optionIndex,
          textchanged: "0",
        });
        valueChanged.current = false;
        return;
      }
    }

    if (newValue !== undefined) {
      if (!ev && valueChanged.current && needProof() && newValue !== undefined) {
        const val = EditStyle_Calendar === 2 ? getDateBack(newValue) : { label: newValue };
        props.onEdit({
          ...editData,
          value: val.label,
          tag: val.id,
        });
        valueChanged.current = false;
        return;
      }

      if (!ev && valueChanged.current && newValue !== undefined) {
        const val = EditStyle_Calendar === 2 ? getDateBack(newValue) : { label: newValue };
        props.onEdit({
          ...editData,
          value: val.label,
          tag: val.id,
        });
        valueChanged.current = false;
        return;
      }
    }
  }

  // функция выбора маски по стилю и типу
  function getMask(dataType, editStyle) {
    if (props.editMask && EditStyle_Calendar === 0) {
      return props.editMask.replaceAll("#", "0");
    }
    if (dataType === "2003") {
      return /^\d+[-]?[0-5]?[0-9]?$/;
    }
    if (dataType === "byte") {
      return /^[0]*[0-9]$|^[0]*[0-9][0-9]$|^[0]*1[0-9][0-9]$|^[0]*2[0-4][0-9]$|^[0]*25[0-5]$/;
    }
    if (dataType === "int" || dataType === "5" || dataType === "2" || dataType === "3") {
      return /^\d+$/;
    }
    if (dataType === "0" || dataType === "17") {
      return Number;
    }
    if (editStyle & 2 || dataType === "date") {
      return Date;
    }

    return /[.\n]*/;
  }

  // функция установки количества нулей после запятой
  function getScale(dataType) {
    if (dataType === "2003") return 2;
    if (dataType === "0") return 2;
    if (dataType === "17") return 3;
    return 0;
  }

  // функция установки дробного разделителя
  function getRadix(dataType, editStyle) {
    if (dataType === "2003") {
      return "-";
    }
    if (dataType === "0" || dataType === "17") {
      return ",";
    }
    if (editStyle & 2) {
      return ".";
    }
  }

  function getHint() {
    if (props.showHint) {
      if (props.hintText !== undefined && props.hintText !== "") {
        return props.hintText;
      } else {
        return input.current ? input.current.value : "";
      }
    }
    return "";
  }

  function needProof() {
    if (EditStyle_Calendar) return false;
    return (props.type || EditStyle_PickList || EditStyle_Ellipsis) && props.datatype >= 12;
  }

  function closeAllPopovers() {
    if (openList) {
      setOpenList(false);
    }
    if (anchorElForCheckList) {
      handleCloseForCheckList();
    }
    if (anchorElDate) {
      handleCloseDate();
    }
  }

  function onArrowDown(ev) {
    if (openList || anchorElForCheckList || anchorElDate) {
      ev.stopPropagation();
      if (ev.altKey) closeAllPopovers();
      setTimeout(() => {
        input.current.focus();
      });
      return;
    }
    if (ev.altKey && popover) {
      ev.stopPropagation();
      if (EditStyle_Calendar) {
        buttonCalendar.current.click();
        return;
      }
      if (popover) {
        buttonPickList.current.click();
        return;
      }
    }
  }

  function onF4keyDown(ev) {
    if (popover) {
      ev.stopPropagation();
      if (EditStyle_Calendar) {
        buttonCalendar.current.click();
        return;
      }
      if (popover) {
        buttonPickList.current.click();
        return;
      }
    }
  }

  const onKeyEnter = (ev) => {
    if (openList || anchorElForCheckList || anchorElDate) {
      ev.stopPropagation();
      return;
    }
    if (ev.ctrlKey) {
      openAfterRender(0);
      return;
    }
    valueChanged.current = true;
    input.current?.blur();
  };

  const onKeyEscape = () => {
    valueChanged.current = false;
    closeAllPopovers();
  };

  const onKeyS = (ev) => {
    if (ev.ctrlKey) {
      ev.preventDefault();
      input.current?.blur();
    }
  };

  const onKeyArrow = (ev) => {
    onArrowDown(ev);
  };

  const onKeyF4 = (ev) => {
    if (openList || anchorElForCheckList || anchorElDate) {
      closeAllPopovers();
    } else onF4keyDown(ev);
  };

  const onKeyDefault = (ev) => {
    if (EditStyle_ReadOnly === 64 || props.readonly) {
      if (ev.key.length === 1) {
        setFilter(`${filter}${ev.key}`);
      } else if (ev.code === "Backspace") {
        setFilter(filter.slice(0, -1));
      }
    }
  };

  function onKeyDown(ev) {
    const keys = {
      NumpadEnter: onKeyEnter,
      Enter: onKeyEnter,
      Escape: onKeyEscape,
      KeyS: onKeyS,
      ArrowUp: onKeyArrow,
      ArrowDown: onKeyArrow,
      F4: onKeyF4,
    };

    keys[ev.code]?.(ev) || onKeyDefault(ev);
  }

  function changeInputValue(value) {
    valueChanged.current = true;

    props.onChange &&
      props.onChange(value, {
        valueChanged: valueChanged,
        datatype: props.datatype,
        addInfo: props.addInfo,
        type: props.type,
        ObjRef: props.objref,
      });

    setInputValue(value);
  }

  return (
    <div
      ref={mainBlock}
      componenteditor="true"
      style={{
        height: "100%",
        width: "100%",
        ...props.styleComponent,
      }}
    >
      {/* компонент со списком */}
      <Autocomplete
        disabled={Boolean(props.disabled || props.HardCodeReadOnlyForEditor)}
        data-needproof={`${needProof()}`}
        // свободное редактирование
        freeSolo
        // внутреннее значение
        value={value && value.label ? value : ""}
        onChange={(ev, newValue) => {
          if (!newValue) return;
          if (ev.code === "Enter" || ev.code === "NumpadEnter") {
            if (openList || anchorElForCheckList || anchorElDate) {
              onEditValue(ev, newValue, "0");
              closeAllPopovers();
            }
            return;
          }
          if (ev.code === "Escape") {
            return;
          }
          onEditValue(ev, newValue);
          // setValue(newValue);
        }}
        // внешнее значение
        inputValue={inputValue ? inputValue : ""}
        // onInputChange={(ev, newValue) => {
        //   if (ev && ev.key !== "Enter") setInputValue(newValue);
        //   valueChanged.current = true;
        // }}
        // вызов функции редактирования при нажатии на enter
        onKeyDown={onKeyDown}
        ref={autoCompleteValue}
        sx={{
          height: "100%",
        }}
        // данные для выпадающего списка
        options={
          paramValuesItems.length
            ? paramValuesItems
            : [{ label: `Ничего не найдено... Фильтр: ${filter}`, disabled: true }]
        }
        renderOption={(props, option) => {
          if (EditStyle_CustomColorList || EditStyle_ColorBox) {
            // опции с цветными квадратиками
            return (
              <li
                {...props}
                title={option.label}
                style={{
                  height: "30px",
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  fontSize: "13px",
                  borderBottom: "1px solid #eeeeee",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
                key={`${option.label}-${option.id}-${props.id}`}
              >
                <div
                  style={{
                    height: "25px",
                    width: "25px",
                    border: "1px solid",
                    marginRight: "5px",
                    backgroundColor: option.color,
                  }}
                ></div>
                {option.label}
              </li>
            );
          }
          return (
            <li
              {...props}
              title={option.label}
              style={{
                height: "30px",
                fontSize: "13px",
                width: "100%",
                borderBottom: "1px solid #eeeeee",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
              key={`${option.label}-${option.id}-${props.id}`}
            >
              {option.label}
            </li>
          );
        }}
        // выбор текста из опций
        getOptionLabel={(option) => {
          return option.label ? option.label : "";
        }}
        // правило равенства опции и заданного внутреннего значения
        isOptionEqualToValue={(option, value) => {
          return option.id === value.id || option.label === value.label;
        }}
        // правило получения выключенных опций
        getOptionDisabled={(item) => {
          return item.disabled === true;
        }}
        noOptionsText="Ничего не найдено..."
        // состояние открытия выпадающего списка
        open={openList}
        // передача в выпадающий список функции на click по значению
        slotProps={{
          popper: {
            placement: "bottom-start",
            style: {
              minWidth: autoCompleteValue.current ? `${autoCompleteValue.current.clientWidth}px` : "200px",
              maxWidth: `${document.body.clientWidth / 2}px`,
              width: `fit-content`,
              border: "1px solid #cccccc",
              borderRadius: "6px",
              boxShadow: "5px 5px 5px #dddddd",
            },
            sx: {
              [`& .${autocompleteClasses.listbox}`]: {
                listStyle: "none",
                margin: "0",
                padding: "0",
                minWidth: "fit-content",
                position: "relative",
              },
            },
            onClick: (ev) => {
              if (ev.target.tagName === "LI") setOpenList(false);
            },
          },
        }}
        // отрисовка маскированного input с необходимыми параметрами
        renderInput={(params) => {
          // use ref to get access to internal "masked = ref.current.maskRef"
          delete params.InputProps.endAdornment;
          delete params.InputProps.startAdornment;

          return (
            // главный контейнер
            <Tooltip arrow placement="bottom-start" title={getHint()}>
              <div
                onFocus={(ev) => {
                  if (!ev.target?.closest("[componenteditor]")) return;
                  onFocus(ev);
                }}
                onBlur={onBlur}
                {...params.InputProps}
                style={{
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  paddingRight: "0px",
                  paddingLeft: "3px",
                  backgroundColor: "#ffffff",
                }}
              >
                {/* контейнер с кнопками перед input */}
                <div
                  ref={startContentRef}
                  style={{
                    display: "flex",
                    height: "100%",
                    alignItems: "center",
                  }}
                >
                  {/* чекбокс */}
                  <CheckBoxMultiCheck
                    onEdit={(data) => {
                      props.onEdit({
                        path: props.path,
                        type: props.type,
                        datatype: props.datatype,
                        name: props.name,
                        checked: data.CheckState,
                        addInfo: props.addInfo,
                      });
                    }}
                    parentProps={props}
                    MultiCheckSet={undefined}
                    CheckState={props.checked}
                    // видимость
                    display={EditStyle_Check ? "" : "none"}
                    disabled={Boolean(props.disabled || props.HardCodeReadOnlyForEditor)}
                  />
                </div>
                {/* маскированный input */}
                <div
                  style={{
                    display: "flex",
                    flex: "1 1 auto",
                    alignItems: "center",

                    ...props.styleComponent,
                  }}
                >
                  {props.datatype <= 12 && props.datatype >= 7 && !props.editMask ? (
                    <div
                      style={{
                        height: "100%",
                        width: "100%",
                      }}
                    >
                      <textarea
                        {...params.inputProps}
                        inputeditor="true"
                        placeholder={props.placeholderVisible === "1" ? placeholder : ""}
                        className={"textfieldinput"}
                        style={{
                          ...styleInput,
                          resize: "none",
                          wrap: "soft",
                          height: "100%",
                          paddingTop: "3px",
                        }}
                        // type={"text"}
                        onDoubleClick={() => {
                          if (
                            modalButton.current &&
                            modalButton.current.style.display !== "none" &&
                            props.doubleClickOnInput
                          ) {
                            modalButton.current.click();
                          }
                        }}
                        readOnly={EditStyle_ReadOnly === 64 || props.readonly}
                        value={inputValue ? inputValue : ""}
                        title={inputValue}
                        ref={(el) => {
                          input.current = el;
                          params.inputProps.ref.current = el;
                        }}
                        onKeyDown={(ev) => {
                          if (ev.shiftKey) {
                            switch (ev.code) {
                              case "NumpadEnter":
                              case "Enter":
                                ev.stopPropagation();
                                break;
                              default:
                                break;
                            }
                          }
                          return;
                        }}
                        // onChange для инпута
                        onChange={(ev) => {
                          if (inputValue !== ev.target.value) {
                            valueChanged.current = true;
                          }
                          props.onChange &&
                            props.onChange(ev.target.value, {
                              valueChanged: valueChanged,
                              datatype: props.datatype,
                              addInfo: props.addInfo,
                              type: props.type,
                              ObjRef: props.objref,
                            });
                          setInputValue(ev.target.value);
                        }}
                      />
                    </div>
                  ) : (
                    <IMaskInput
                      {...params.inputProps}
                      inputeditor="true"
                      placeholder={props.placeholderVisible === "1" && !props.editMask ? placeholder : ""}
                      // onMouseEnter={onOpenToolTip}
                      // onMouseLeave={onCloseToolTip}
                      className={"textfieldinput"}
                      style={styleInput}
                      type={"text"}
                      onDoubleClick={() => {
                        if (
                          modalButton.current &&
                          modalButton.current.style.display !== "none" &&
                          props.doubleClickOnInput
                        ) {
                          modalButton.current.click();
                        }
                      }}
                      readOnly={EditStyle_ReadOnly === 64 || props.readonly}
                      // маска
                      mask={getMask(props.datatype, props.editStyle)}
                      // разделитель между тысячами
                      thousandsSeparator=" "
                      padFractionalZeros={true}
                      scale={getScale(props.datatype, props.editStyle)}
                      radix={getRadix(props.datatype, props.editStyle)}
                      // отображение маски в placeholder
                      lazy={false}
                      value={inputValue}
                      title={inputValue}
                      unmask={true}
                      ref={maskRef}
                      overwrite={EditStyle_Calendar ? true : false}
                      // проверка даты
                      validate={(val) => {
                        if (EditStyle_Calendar !== 2) return;
                        if (val.match(/[0-9]{2}.[0-9]{2}.[0-9]{4}/)) {
                          if (val.match(dateRegexp)) {
                            input.current.style.outline = "";
                            input.current.title = "";
                          } else {
                            input.current.style.outline = "1px solid red";
                            input.current.title = "Укажите корректную дату";
                          }
                        } else {
                          input.current.style.outline = "";
                          input.current.title = "";
                        }
                      }}
                      inputRef={(el) => {
                        input.current = el;
                        params.inputProps.ref.current = el;
                      }}
                      // onChange для инпута
                      onAccept={(maskValue, mask, ev) => {
                        if (
                          (inputValue === undefined || inputValue === "") &&
                          (maskValue === "" || maskValue === "..")
                        ) {
                          return;
                        }
                        if (inputValue !== maskValue) valueChanged.current = true;
                        props.onChange &&
                          props.onChange(maskValue, {
                            valueChanged: valueChanged,
                            datatype: props.datatype,
                            addInfo: props.addInfo,
                            type: props.type,
                            ObjRef: props.objref,
                          });
                        if (ev && ev.data === null) {
                          input.current.style.outline = "";
                          input.current.title = "";
                        }
                        if (EditStyle_Calendar || props.datatype === "date") return;
                        setInputValue(maskValue);
                      }}
                    />
                  )}
                </div>

                {/* контейнер с кнопками после input */}
                <div
                  ref={endContentRef}
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  {/* календарь */}
                  <CalendarButton
                    buttonRef={buttonCalendar}
                    id={idDate}
                    handleClick={handleClickDate}
                    itemID={props.id}
                    EditStyle_Calendar={EditStyle_Calendar}
                    disabled={Boolean(props.disabled || props.HardCodeReadOnlyForEditor)}
                  />
                  {/* кнопки +- */}
                  <UpDownButtons
                    EditStyle_UpDown={EditStyle_UpDown}
                    incValue={incValue}
                    decValue={decValue}
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    valueChanged={valueChanged}
                    disabled={Boolean(props.disabled || props.HardCodeReadOnlyForEditor)}
                  />
                  {/* кнопка модального окна */}
                  <ModalButton
                    props={{
                      ...props,
                      refButton: modalButton,
                      EditStyle_Ellipsis: EditStyle_Ellipsis,
                      EditStyle_Calendar: EditStyle_Calendar,
                      inputValue: inputValue,
                      value: value,
                      input: input,
                      selection: selection,
                      setValue: setValue,
                      setInputValue: setInputValue,
                      setSelection: setSelection,
                      GetDialogParam: GetDialogParam,
                      openModalWindow: openModalWindow,
                      closeList: () => {
                        if (autocompleteList) setOpenList(false);
                      },
                      disabled: Boolean(props.disabled || props.HardCodeReadOnlyForEditor),
                    }}
                  />
                  {/* выпадающий список */}
                  <PickListEditor
                    buttonRef={buttonPickList}
                    id={idForCheckList}
                    itemId={props.id}
                    EditStyle_PickList={popover && !EditStyle_Calendar && !props.details}
                    onPickListClick={onPickListClick}
                    disabled={Boolean(props.disabled || props.HardCodeReadOnlyForEditor)}
                  />
                  {props.details ? (
                    <DetailsButton
                      buttonRef={buttonDetails}
                      autoCompleteValue={autoCompleteValue}
                      details={props.details}
                      inputValue={inputValue}
                      module={props.type}
                      setInputValue={setInputValue}
                      setValue={setValue}
                      valueChanged={valueChanged}
                    />
                  ) : null}
                  {EditStyle_CheckList && !EditStyle_CustomCheckList ? (
                    <CheckboxList
                      // objref={checkListObjRef.current}
                      id={idForCheckList}
                      open={openForCheckList}
                      anchorEl={anchorElForCheckList}
                      handleClose={handleCloseForCheckList}
                      items={paramValuesItems}
                      checked={checkedList}
                      handleToggle={handleToggle}
                    />
                  ) : null}
                  {EditStyle_CustomCheckList && !EditStyle_CheckList ? (
                    <CustomCheckBoxList
                      id={idForCheckList}
                      open={openForCheckList}
                      anchorEl={anchorElForCheckList}
                      handleClose={handleCloseForCheckList}
                      items={paramValuesItems}
                      onEditCustomCheckList={props.onEditCustomCheckList}
                    />
                  ) : null}
                </div>
                {/* popover для выбора даты */}
                {EditStyle_Calendar ? (
                  <DateCalendarEditor
                    id={idDate}
                    open={openDate}
                    anchorEl={anchorElDate}
                    handleClose={handleCloseDate}
                    getDateInFormat={getDateInFormat}
                    inputValue={inputValue}
                    objRef={value.id}
                    setInputValue={setInputValue}
                    valueChanged={valueChanged}
                    onEditValue={onEditValue}
                  />
                ) : null}
              </div>
            </Tooltip>
          );
        }}
      />
    </div>
  );
}
